/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, HStack, Text, Tooltip } from '@chakra-ui/react';
import { UrgentIcon } from '../../../../constants/IconData';
import { AuditContext } from '../../../../providers/AuditProvider';
import { createColumnHelper } from '@tanstack/react-table';
import AppColors from '../../../../constants/AppColors';
import { toTitleCase } from '../../../../utils/common.utils';
import {
  ASSIGNED_AUDITS_ORDERING,
  getAuditStatus,
} from '../../../../constants/constants';
import useCQMediaQuery from '../../../../hooks/useCQMediaQuery';
import {
  createTableProps,
  paginationProps,
} from '../../../../utils/assigned_audit.util';
import { format, formatDistanceStrict } from 'date-fns';

import '../style.css';
import { CQReactTable } from '@laxmimanogna/code-quick-components';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../../constants/Routes';
import FileViewAction from '../FileViewAction';

const columnHelper = createColumnHelper();

const ArchivedTable = () => {
  const auditContext = useContext(AuditContext);
  const [ordering, setOrdering] = useState('');
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  // media query
  const canScroll = useCQMediaQuery({
    lg: false,
    md: false,
    sm: true,
  });

  const navigate = useNavigate();
  const { isUpdating, getAuditArchived, filterParams } =
    auditContext;
  
  // SCROLL CHANGE: Auto-restore
  const PAGE_NAME = "Assign Audit";
  const TABLE_NAME = "Archived Audit";

  useEffect(() => {
    if (auditContext.auditArchivedList.length > 0) {
      const itemId = getAuditArchived.restoreNavigationState(PAGE_NAME, TABLE_NAME, true);
      if (itemId) {
        setHighlightedRowId(itemId);
        setTimeout(() => {
          setHighlightedRowId(null);
        }, 2000);
      }
    }
  }, [auditContext.auditArchivedList]);

  useEffect(() => {
    let searching = !!(filterParams.searchText && filterParams.searchText.trim());
    getAuditArchived.loadAPI("Assign Audit", "Archived Audit", searching);
  }, [filterParams, getAuditArchived.showOrdering]);

  useEffect(() => {
    if (ASSIGNED_AUDITS_ORDERING.includes(ordering)) {
      getAuditArchived.setParams({ ordering });
    }
  }, [ordering]);

  const renderActionComponent = auditUpload => {
    return (
      <HStack justifyContent={'end'}>
        <Button
          px={12}
          size={'sm'}
          borderRadius={'full'}
          bgColor={AppColors.secondary}
          color={'white'}
          w={'150px'}
          fontSize={'16px'}
          h={'40px'}
          outline={`2px`}
          _hover={{
            transition: 'all .3s ease',
            bgColor: AppColors.white,
            color: AppColors.secondary,
            outline: `2px solid ${AppColors.secondary}`,
          }}
          onClick={() => {
            getAuditArchived.saveNavigationState(
              PAGE_NAME, 
              TABLE_NAME, 
              auditUpload.id
            );
            navigate(
              ROUTES.VIEW_AUDIT_SHEET.replace(':uploadId', auditUpload.id)
            );
          }}
        >
          View
        </Button>
        <FileViewAction auditUpload={auditUpload} chart_id={auditUpload?.id}/>
      </HStack>
    );
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: 'batch_id',
        header: () => 'Batch',
        cell: info => {
          const allTooltip = info.getValue().batch_name || info.getValue().batch_id;
            
          const tp = { label: allTooltip, placement: 'bottom', hasArrow: true, bg: 'gray.800', color: 'white', borderRadius: 'md', p: 2, openDelay: 80 };
          return (
            <HStack
              textAlign={'left'}
              justifyContent={'flex-start'}
              alignItems={'center'}
              spacing={3}
            >
              {info.row.original?.urgent ? <UrgentIcon /> : <Box ml={2} />}
              <Tooltip {...tp}>
                <Text 
                textAlign={'left'} 
                isTruncated 
                maxW="125px" 
                cursor="default"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                display="block"
                lineHeight="1.2" 
                >
                  {info.getValue().batch_name || info.getValue().batch_id}
                </Text>
              </Tooltip>
            </HStack>
          );
        },
      }),
      columnHelper.accessor('upload_id', {
        header: () => 'Upload #',
        id: 'chart_id',
        cell: info => {
          return (
            <HStack
              textAlign={'left'}
              justifyContent={'flex-start'}
              alignItems={'center'}
              spacing={3}
            >
              <Text>{info.renderValue()}</Text>
            </HStack>
          );
        },
      }),
      columnHelper.accessor(row => row.uploaded_date, {
        id: 'upload_date',
        header: () => <span>Chart Upload</span>,
        cell: info => {
          const cellValue = info.getValue();
          if (!cellValue) {
            return 'N/A';
          }

          return format(new Date(cellValue), 'MM/dd/yyyy');
        },
      }),
      columnHelper.accessor(p => p.client_name?.first_name, {
        header: () => 'Uploaded By',
        id: 'client',
        cell: info => {
          const name = toTitleCase(
            `${info.row.original.client_name?.first_name} ${info.row.original.client_name?.last_name}`
          );
          return <Text>{name}</Text>;
        },
      }),
      columnHelper.accessor('specialties', {
        header: () => 'Specialties',
        id: 'specialty__name',
        cell: info => {
          const specialties = info.row.original.specialties?.name;

          if (!specialties) {
            return 'N/A';
          }
          return specialties === 'ent' ? 'ENT' : toTitleCase(specialties);
        },
      }),
      columnHelper.accessor('total_page', {
        header: () => 'Pages',
        id: 'total_pages',
      }),
      columnHelper.accessor('status', {
        header: () => 'Status',
        id: 'status',
        cell: info => <Text>{getAuditStatus(info.row.original?.status)}</Text>,
      }),
      columnHelper.accessor('updated_date', {
        header: () => 'Last Updated',
        id: 'updated_date',
        cell: info => {
          let updatedAt = info.row.original?.archived_date
            ? info.row.original?.archived_date
            : info.row.original?.updated_date;

          updatedAt = updatedAt
            ? formatDistanceStrict(new Date(updatedAt), new Date(), {
              addSuffix: true,
            })
            : 'N/A';

          return (
            <Text>
              {updatedAt !== 'N/A' ? toTitleCase(updatedAt) : updatedAt}
            </Text>
          );
        },
      }),
      columnHelper.accessor('actions', {
        header: () => 'Actions',
        enableSorting: false,
        cell: info => {
          return renderActionComponent(info.row.original);
        },
      }),
    ],
    [
      auditContext.auditArchivedList,
      auditContext.isfettchingAuditArchived
    ]
  );

  function handlePreviousPage() {
    auditContext.getAuditArchived.loadPrevious("Assign Audit", "Archived Audit");
  }

  function handleNextPage() {
    auditContext.getAuditArchived.loadNext("Assign Audit", "Archived Audit");
  }

  function handleSizeChange(size) {
    auditContext.getAuditArchived.loadSize("Assign Audit", "Archived Audit", size );
  }

  function handlePageChangeByNumber(pageNumber) {
    auditContext.getAuditArchived.loadPageByNumber("Assign Audit", "Archived Audit", pageNumber);
  }

  
  return (
    <Box
      mt={3}
      pointerEvents={isUpdating ? 'none' : 'inherit'}
      overflowX={canScroll ? 'scroll' : 'inherit'}
    >
      <Box>
        <CQReactTable
          manualSortBy={true}
          isLoading={auditContext.isfettchingAuditArchived}
          data={auditContext.auditArchivedList}
          columns={columns}
          {...createTableProps(highlightedRowId)}
          {...paginationProps(getAuditArchived)}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          onSizeChange={handleSizeChange}
          onPageChangeByNumber={handlePageChangeByNumber}
          onAsc={header => {
            setOrdering(header);
          }}
          onDesc={header => {
            setOrdering(`-${header}`);
          }}
        />
      </Box>
    </Box>
  );
};
export default ArchivedTable;
